import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import Container from "@material-ui/core/Container";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import { toast } from 'react-toastify';
import { useHistory } from "react-router-dom";
import StyledCard from 'components/StyledCard';
import styled from "styled-components";
import PageLayout from "components/PageLayout";
import BackButton from "components/BackButton";
import TextField from '@material-ui/core/TextField';
import DayJS from 'react-dayjs';
import useAuth from "../../hooks/useAuth";
import useDb from "../../hooks/useDb";
import useFormDataApi from "../../hooks/useFormDataAPI"
import { useForm } from 'react-hook-form';
import useAppointment from "../../hooks/useAppointment";
import useAPI from "hooks/useAPI";
import useStyles from './styles';
import Brightness1Icon from '@material-ui/icons/Brightness1';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import { useAlert } from "../../providers/AlertService";
import useGlobalLoading from "hooks/useGlobalLoading"
import Compressor from "compressorjs";

const DeleteButton = styled.button`
  position: absolute;
  color: red;
  font-weight: bold;
  font-size: 1.5rem;
  top: 10px;
  right: 10px;
`;

const Picture = styled.img`
  width: 100%;
  height: 100%;
`;


const RAndRHub = () => {
  const { register, handleSubmit, control } = useForm();
  const { setIsLoading } = useGlobalLoading();
  const formDataApi = useFormDataApi();
  const imgRef = useRef();
  const db = useDb();
  const params = useParams();
  const history = useHistory();
  const { appointmentId } = params;
  const { appointment, setAppointment } = useAppointment(appointmentId);
  const [forms, setForms] = useState([]);
  const styles = useStyles();
  const instance = useAPI();
  const alert = useAlert();

  const formsMetadata = [
    { name: 'repairedAndReplacedPage1', label: 'page 1', path: `/form/repaired-and-replaced/1/1/${appointmentId}`, version: process.env.REACT_APP_NGAT_VERSION },
    { name: 'repairedAndReplacedPage2', label: 'page 2', path: `/form/repaired-and-replaced/1/2/${appointmentId}`, version: process.env.REACT_APP_NGAT_VERSION },
  ]

  const submitEmptyAppointment = reasonForReschedule => {
    setIsLoading(true);

    const data = new FormData();
    data.append('reasonForReschedule', reasonForReschedule.rescheduleReason)
    console.log(data);

    formDataApi
      .post(`/appointments/${appointment._id}/finalize`, data)
      .then(res => {
        toast.success('Successfully submitted images!');
        setAppointment({
          ...appointment,
          completed: true
        });

        history.push('/');
      })
      .catch(err => {
        toast.error(err.message)
      })
      .finally(() => setIsLoading(false));
  }

  const handleAddPicture = async (event) => {
    event.preventDefault();
    try {

      const [picture] = imgRef.current.files;
      const buffer = await picture.arrayBuffer();
      const blob = new Blob([buffer]);

      const a = { ...appointment };
      a.pictures.push({ blob, name: picture.name });

      setAppointment(a);
    }
    catch (err) {
      console.log(err)
    }
  };

  const handleRemovePicture = (pictureIdx) => {
    if (window.confirm('Are you sure you want to delete the data for this form?')) {
      setAppointment({
        ...appointment,
        pictures: [
          ...appointment.pictures.slice(0, pictureIdx),
          ...appointment.pictures.slice(pictureIdx + 1),
        ],
      });
    }
  };

  const submitAppointment = async () => {
    const currentDateObj = new Date();
    const dateSubmitted = currentDateObj.toISOString()
    const initializedForms = await db.forms.where({ appointmentId }).toArray();
    const unreviewedForms = await db.forms.where({ appointmentId, reviewed: 0 }).toArray();

    const data = new FormData();

    const customerFullName = `${appointment.customer.firstName}${!!(appointment.customer.middleName) ? ` ${appointment.customer.middleName}` : ''} ${appointment.customer.lastName}`
    data.append('dateSubmitted', dateSubmitted);
    data.append('customerName', customerFullName);
    data.append('submissions', JSON.stringify([]));
    data.append('enrollmentNumber', appointment.customer.enrollment.enrollmentNumber);

    for (const picture of appointment.pictures) {
      console.log('added picture')
      data.append('images', picture.blob, picture.name)
    }

    if (!initializedForms.length) {
      if (appointment.pictures.length > 0) {
        if (!window.confirm("Are you sure to submit forms with pictures only?")) {
          return alert({ description: 'Must complete at least one form' });
        }
      } else return alert({ description: 'Must complete at least one form' });
    }

    if (unreviewedForms.length) return alert({ description: `The form(s) [${unreviewedForms.map(form => form.type).join(', ')}] were started but not reviewed. Either review them or delete them before submitting.` });

    const submissions = initializedForms.map(form => ({
      name: form.type,
      currentRevision: null,
      version: form.version,
      enrollmentNumber: appointment.customer.enrollment.enrollmentNumber,
      fields: form
    }));

    setIsLoading(true)

    if (appointment.pictures.length > 0) {
      formDataApi.post(
        `/appointments/${appointment._id}/finalize`,
        data
      )
        .then(res => {
          toast.success('Successfully submitted images!');
          setAppointment({
            ...appointment,
            completed: true
          });

          history.push('/');
        })
        .catch(err => {
          toast.error(err.message)
        })
        .finally(() => setIsLoading(false));

      console.log(data)
    }

    if (initializedForms.length) {
      data.delete('submissions');
      data.append('submissions', JSON.stringify(submissions));
      data.delete('images');

      setIsLoading(true)
      formDataApi.post(
        `/appointments/${appointment._id}/finalize`,
        data
      )
        .then(res => {
          toast.success('Successfully finalized appointment!');
          setAppointment({
            ...appointment,
            completed: true
          });

          history.push('/');
        })
        .catch(err => {
          toast.error(err.message)
        })
        .finally(() => setIsLoading(false));
    }
  };

  useEffect(() => {
    Promise.all(formsMetadata.map(async metadata => {
      const result = await db.forms.get({ appointmentId, type: metadata.name, version: metadata.version });

      return {
        ...metadata,
        reviewed: result?.reviewed
      }
    }))
      .then(res => {
        console.log(res)
        setForms(res)
      });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const removeForm = (form) => {
    const result = window.confirm('Are you sure you want to delete the data for this form?');
    if (result === true)
      db.forms
        .where({
          appointmentId,
          type: form.name,
          version: form.version
        })
        .delete()
        .then(setForms(forms => forms.map(f => ({ ...f, reviewed: f.name === form.name ? undefined : f.reviewed }))))
  }

  if (!appointment) return null;

  return (
    <PageLayout title="Repaired & Replaced">
      <Container maxWidth="sm">
        <>
          <Box display="flex" alignItems="center" justifyContent="space-between">
          </Box>
          <h2>Pages:</h2>
          <Box>
            {forms.map(form => {
              console.log(form.name, form.reviewed)
              return <div key={form.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Link to={`${form.path}`} className={styles.formButton}>{form.label}</Link>
                <Brightness1Icon
                  style={{
                    color: form.reviewed === undefined
                      ? 'white'
                      : form.reviewed === 0
                        ? 'yellow'
                        : 'green'
                  }}
                />
                {form.reviewed !== undefined &&
                  <DeleteForeverIcon
                    id={`${form.name}-trash`}
                    onClick={() => removeForm(form)}
                  />}
              </div>
            })}
          </Box>
          <p></p>
        </>
        <form onSubmit={handleSubmit(submitEmptyAppointment)}>
        </form>
      </Container>
    </PageLayout>
  );
};

const Page = () => {
  const { PrivateContent } = useAuth();

  return (
    <PrivateContent title="R&R">
      <RAndRHub />
    </PrivateContent>
  );
};

export default RAndRHub;